import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { ConnectPortalLayout } from "@/components/layout/ConnectPortalLayout";
import { useAuth } from "@/contexts/AuthContext";
import { SEOHead } from "@/components/seo/SEOHead";
import { PAGE_SEO, getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";
import { useTranslation } from "react-i18next";

/**
 * Internal How-It-Works — mirrors public /how-it-works (spec §5).
 * Kept to exactly 3 steps so authed and public views stay aligned.
 */
const STEP_KEYS = ["1", "2", "3"] as const;

function HowItWorksBody({ authed }: { authed: boolean }) {
  const { t } = useTranslation("common");
  return (
    <>
      <section className="py-16 sm:py-20 md:py-24">
        <div className="container mx-auto px-4 max-w-3xl">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            {t("web.howItWorks.title")}
          </h1>
          <p className="text-lg text-muted-foreground mb-12">
            {t("web.howItWorks.intro")}
          </p>

          <ol className="space-y-8">
            {STEP_KEYS.map((n) => (
              <li key={n} className="flex gap-6 items-start">
                <div className="h-12 w-12 rounded-full bg-primary/10 text-primary font-bold text-xl flex items-center justify-center shrink-0">
                  {n}
                </div>
                <div>
                  <h2 className="text-2xl font-semibold mb-1">{t(`web.howItWorks.step${n}Title`)}</h2>
                  <p className="text-muted-foreground">{t(`web.howItWorks.step${n}Body`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {!authed && (
        <section className="py-16 sm:py-20 bg-muted/30 border-y">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">{t("web.howItWorks.ctaTitle")}</h2>
            <p className="text-base text-muted-foreground mb-8">{t("web.howItWorks.ctaBody")}</p>
            <Link to="/connect-portal/auth">
              <Button size="lg">{t("getStarted")}</Button>
            </Link>
          </div>
        </section>
      )}
    </>
  );
}

export default function ConnectHowItWorksPage() {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation("common");
  if (isLoading) return null;

  const seo = (
    <SEOHead
      title={PAGE_SEO.howItWorks.title}
      description={PAGE_SEO.howItWorks.description}
      keywords={PAGE_SEO.howItWorks.keywords}
      canonicalUrl={getCanonicalUrl('/connect-portal/how-it-works')}
      structuredData={getBreadcrumbSchema([
        { name: 'Home', url: 'https://elefa.tel/' },
        { name: 'How It Works', url: 'https://elefa.tel/connect-portal/how-it-works' },
      ])}
    />
  );

  if (user) {
    return (
      <ConnectPortalLayout>
        {seo}
        <HowItWorksBody authed />
      </ConnectPortalLayout>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {seo}
      <nav className="container py-6 flex items-center justify-between border-b">
        <Link to="/connect-portal" className="flex items-center">
          <Logo variant="header" size="sm" asLink={false} withIcon fullName />
        </Link>
        <Link to="/connect-portal/auth">
          <Button size="sm">{t("getStarted")}</Button>
        </Link>
      </nav>
      <HowItWorksBody authed={false} />
      <ConnectFooter />
    </div>
  );
}
